import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, MapPin, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HERO_IMAGES, CONDITION_LABEL, CONDITION_ICON, windDirection } from "@/lib/weatherUtils";

export default function WeatherHero({ data, loading }) {
  const condition = data?.condition || "clear";
  const bg = HERO_IMAGES[condition] || HERO_IMAGES.clear;
  const Icon = CONDITION_ICON[condition] || CONDITION_ICON.clear;

  return (
    <section data-testid="weather-hero" className="relative min-h-screen w-full overflow-hidden hero-gradient">
      <AnimatePresence mode="wait">
        <motion.div
          key={condition}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <img
            src={bg}
            alt={CONDITION_LABEL[condition]}
            className="absolute inset-0 w-full h-full object-cover ken-burns"
            loading="eager"
          />
        </motion.div>
      </AnimatePresence>

      {/* content */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pt-32 lg:pt-40 pb-16 text-white">
        <motion.div
          initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 glass-dark rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.24em] text-white/90" data-testid="live-badge">
            <Radio className="h-3.5 w-3.5 text-[#FBBF24] animate-pulse" />
            Monitoraggio in diretta
          </div>
          <h1 className="mt-6 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight text-shadow-soft" data-testid="hero-title">
            Stazione Meteo <span className="text-[#FBBF24] italic">Palestrina</span>
            <br />
            <span className="text-white/90">— Via Loreto</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/80 font-light max-w-2xl">
            Dati atmosferici in diretta dalla mia stazione personale in Via Loreto. Aggiornati ogni 60 secondi, aperti a tutti.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/dashboard">
              <Button data-testid="hero-cta-live" size="lg" className="rounded-full bg-[#FBBF24] hover:bg-[#f59e0b] text-[#0B132B] font-semibold px-7 h-12">
                Visualizza dati live
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/stazione">
              <Button data-testid="hero-cta-station" size="lg" variant="outline" className="rounded-full bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white h-12 px-7 backdrop-blur">
                Scopri la stazione
              </Button>
            </Link>
          </div>
          <div className="mt-6 flex items-center gap-2 text-sm text-white/75">
            <MapPin className="h-4 w-4 text-[#FBBF24]" />
            Via Loreto, 00036 Palestrina (Roma) — 404 m s.l.m.
          </div>
        </motion.div>

        {/* floating stat panel */}
        <motion.div
          initial={{ opacity: 0, y: 36, filter: "blur(10px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.2, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 lg:mt-20 glass-dark rounded-3xl p-6 md:p-8 max-w-5xl grid grid-cols-2 md:grid-cols-5 gap-6"
          data-testid="hero-stats"
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-white/70">Condizione</p>
            <div className="mt-2 flex items-center gap-2">
              <Icon className="h-6 w-6 text-[#FBBF24]" />
              <span className="text-xl font-medium">{CONDITION_LABEL[condition]}</span>
            </div>
          </div>
          <HeroStat label="Temperatura" value={loading || !data ? "—" : `${data.temperature}°`} />
          <HeroStat label="Umidità" value={loading || !data ? "—" : `${data.humidity}%`} />
          <HeroStat label="Vento" value={loading || !data ? "—" : `${data.wind_speed} km/h`} sub={data ? windDirection(data.wind_direction) : ""} />
          <HeroStat label="Pressione" value={loading || !data ? "—" : `${data.pressure} hPa`} />
        </motion.div>
      </div>
    </section>
  );
}

function HeroStat({ label, value, sub }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.24em] text-white/70">{label}</p>
      <p className="mt-2 font-display text-3xl md:text-4xl font-light">{value}</p>
      {sub && <p className="text-xs text-white/60 mt-0.5">{sub}</p>}
    </div>
  );
}
